import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  FormGroup,
  Label,
} from "reactstrap";
import { post, put, get } from "helpers/api_helper";
import {
  GET_ALL_CONTACTS,
  GET_CONTACTS_COUNT_DATA,
  UPDATE_CONTACT_STATUS,
  GET_ALL_VENDORS_LIGHT_API,
} from "constants/api_constants";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import SummaryCards from "components/SummaryCards";
import CustomPagination from "components/CustomPagination";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { formatToPascal } from "helpers/utils/nameUtils";
import * as api from "../../constants/api_constants";

// Custom spinner component
const LoadingSpinner = () => (
  <div
    className="spinner-border spinner-border-sm text-primary ms-2"
    role="status"
  >
    <span className="sr-only">Loading...</span>
  </div>
);

function Contacts() {
  const navigate = useNavigate();
  const [contactsData, setContactsData] = useState({
    contacts: [],
    error: "",
  });
  console.log("contactsData", contactsData);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterBy, setFilterBy] = useState("All");
  const [apiStatus, setApiStatus] = useState({
    loading: false,
    error: "",
    errorContactId: null,
  });
  const [contactsCount, setContactsCount] = useState({
    activeCount: 0,
    inactiveCount: 0,
    primaryContacts: 0,
    totalCounts: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    sortBy: "id",
    direction: "DESC",
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const timerRef = useRef(null);
  const [vendors, setVendors] = useState([]);
  console.log("vendors", vendors);

  const [customers, setCustomers] = useState([]);
  console.log("customers", customers);

  const fetchCounts = useCallback(async () => {
    try {
      setApiStatus((prev) => ({
        ...prev,
        loading: true,
        error: "",
        errorContactId: null,
      }));
      const countData = await get(`${GET_CONTACTS_COUNT_DATA}?t=${Date.now()}`);
      console.log("Counts API Request: GET", GET_CONTACTS_COUNT_DATA);
      console.log("Counts API Response (Raw):", countData);

      if (countData?.status) {
        setContactsCount({
          activeCount: countData.activeCount || 0,
          inactiveCount: countData.inactiveCount || 0,
          primaryContacts: countData.primaryContacts || 0,
          totalCounts: countData.totalCounts || 0,
        });
        setApiStatus((prev) => ({ ...prev, loading: false, error: "" }));
      } else {
        throw new Error(
          countData.message || "Invalid counts response structure"
        );
      }
    } catch (error) {
      console.error("Error fetching contact counts:", error);
      setApiStatus((prev) => ({
        ...prev,
        loading: false,
        error: `Failed to fetch contact counts: ${error.message}`,
      }));
      setContactsCount({
        activeCount: 0,
        inactiveCount: 0,
        primaryContacts: 0,
        totalCounts: 0,
      });
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      setApiStatus((prev) => ({ ...prev, loading: true, error: null }));
      const response = await get(GET_ALL_VENDORS_LIGHT_API);
      console.log("Vendors API Response:", response);
      if (response && response.status && Array.isArray(response.data)) {
        const updatedData = response.data.map((item) => ({
          value: item.vendorId || "",
          label:
            item.companyName && item.companyName.trim() !== ""
              ? `${item.companyName.trim()} - (${item.vendorCode || "N/A"})`
              : `${item.email || item.vendorCode || "N/A"} - (${
                  item.vendorCode || "N/A"
                })`,
          companyName: item.companyName || "",
        }));
        console.log("Formatted Vendors:", updatedData);
        setVendors(updatedData);
      } else {
        throw new Error("Invalid vendor response");
      }
    } catch (error) {
      console.error("Fetch Vendors Error:", error.message, error.stack);
      showErrorToast("Failed to load vendors");
      setApiStatus((prev) => ({ ...prev, error: "Failed to load vendors" }));
      setVendors([]);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      const response = await get(api.GET_ALL_CUSTOMERS_LIGHT_API);
      console.log("response", response);
      const updatedCustData = response.map((item) => ({
        value: item.customerId,
        label:
          item.companyName && item.companyName.trim() !== ""
            ? `${item.companyName.trim()} - (${item.customerCode})`
            : `${item.email || item.customerCode} - (${item.customerCode})`,
        companyName: item.companyName || "",
        primaryContactName: item.primaryContactName || "",
      }));
      console.log("Formatted Customers:", updatedCustData);
      setCustomers(updatedCustData);
    } catch (error) {
      console.error("Fetch Customers Error:", error);
      showErrorToast("CUSTOMERS FETCH FAIL");
    }
  }, []);

  useEffect(() => {
    fetchVendors();
    fetchCustomers();
  }, []);

  const fetchContactsData = useCallback(
    async (query = "", page = 1, filter = filterBy) => {
      try {
        setApiStatus((prev) => ({
          ...prev,
          loading: true,
          error: "",
          errorContactId: null,
        }));

        const payload = {
          searchKeyword: query.trim() ?? "",
          pageSize: 10,
          page: page - 1,
          sort: sortConfig.direction.toLowerCase(),
          contactRelatedTo:
            filter === "All"
              ? ""
              : filter === "None"
              ? null
              : filter.toLowerCase(),
        };

        const data = await post(GET_ALL_CONTACTS, payload);
        console.log("Contacts API Response:", data);

        if (
          data &&
          data.status &&
          data.pageResponse &&
          data.pageResponse.data
        ) {
          setContactsData((prev) => ({
            ...prev,
            contacts: data.pageResponse.data.map((contact) => ({
              ...contact,
              status: [0, 1].includes(Number(contact.status))
                ? Number(contact.status)
                : 1,
            })),
            error: "",
          }));

          setTotalPages(data.pageResponse.totalPages || 0);
        } else {
          throw new Error(
            data.message || "Invalid contacts response structure"
          );
        }
      } catch (error) {
        console.error("Fetch Contacts Error:", error);
        setContactsData((prev) => ({
          ...prev,
          contacts: [],
          error: error.message || "Error fetching contacts",
        }));
      } finally {
        setApiStatus((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    },
    [sortConfig, filterBy]
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchContactsData(searchQuery, page, filterBy);
    },
    [fetchContactsData, searchQuery, filterBy]
  );

  const handleSort = useCallback((columnName) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.sortBy === columnName) {
        return {
          sortBy: columnName,
          direction: prevConfig.direction === "ASC" ? "DESC" : "ASC",
        };
      }
      return {
        sortBy: columnName,
        direction: "ASC",
      };
    });
  }, []);

  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "ASC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "DESC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
      </span>
    );
  };

  const columnSortMap = {
    "Contact ID": "id",
    "Contact Full Name": "firstName",
    "Customer/Vendor": "contactRelatedTo",
    Email: "email",
    Mobile: "phoneNumber",
    Designation: "designation",
    Status: "status",
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Contact ID",
          "Contact Full Name",
          "Customer/Vendor",
          "Email",
          "Mobile",
          "Designation",
          "Status",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows
                column={columnSortMap[header]}
                currentSort={sortConfig}
              />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  const toggleContactStatus = useCallback(
    async (id, currentStatus) => {
      try {
        setApiStatus((prev) => ({
          ...prev,
          loading: true,
          error: "",
          errorContactId: id,
        }));

        const validatedStatus = [0, 1].includes(Number(currentStatus))
          ? Number(currentStatus)
          : 1;
        const newStatus = validatedStatus === 1 ? 0 : 1;

        // Optimistically update the UI
        setContactsData((prev) => ({
          ...prev,
          contacts: prev.contacts.map((contact) =>
            contact.id === id ? { ...contact, status: newStatus } : contact
          ),
          error: "",
        }));

        console.log(
          "Status Update Request: PUT",
          UPDATE_CONTACT_STATUS(id, newStatus)
        );
        const response = await put(UPDATE_CONTACT_STATUS(id, newStatus));
        console.log(
          "Status Update Response:",
          JSON.stringify(response, null, 2)
        );

        if (response && response.status && response.data) {
          if (response.data.status === newStatus) {
            await fetchCounts(); // Update counts after successful status update
            showSuccessToast(
              `Contact status updated to ${
                newStatus === 1 ? "Active" : "Inactive"
              }`
            );
          } else {
            throw new Error(
              `Backend returned incorrect status: ${response.data.status} for contact ID ${id}. Expected: ${newStatus}.`
            );
          }
        } else {
          throw new Error(
            response.message || "Failed to update contact status"
          );
        }
      } catch (error) {
        console.error("Error updating contact status:", error);
        // Rollback optimistic update
        setContactsData((prev) => ({
          ...prev,
          contacts: prev.contacts.map((contact) =>
            contact.id === id ? { ...contact, status: currentStatus } : contact
          ),
          error: error.message || "Error updating contact status",
        }));
        let errorMessage = error.message || "Failed to update contact status";
        if (error.message.includes("403")) {
          errorMessage =
            "403 Forbidden: Check authentication token or API permissions";
        } else if (error.message.includes("401")) {
          errorMessage = "401 Unauthorized: Please log in again";
        }
        showErrorToast(errorMessage);
        setApiStatus((prev) => ({ ...prev, error: errorMessage }));
      } finally {
        setApiStatus((prev) => ({
          ...prev,
          loading: false,
          errorContactId: null,
        }));
      }
    },
    [fetchCounts]
  );

  const handleSearch = useCallback(
    (e) => {
      const query = e.target.value || "";
      setSearchQuery(query);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        fetchContactsData(query?.trim(), 1, filterBy);
      }, 300);
    },
    [fetchContactsData, filterBy]
  );

  const handleFilter = useCallback(
    (filter) => {
      setFilterBy(filter);
      setCurrentPage(1);
      fetchContactsData(searchQuery, 1, filter);
    },
    [fetchContactsData, searchQuery]
  );

  const handleNavigation = useCallback(
    async (contactId, status, route) => {
      console.log(`Attempting to navigate to ${route} contact ID: ${contactId}, Status: ${status}`);
      try {
        // Optional: Pre-check contact existence (uncomment if API endpoint is available)
        // const response = await get(`/api/contacts/${contactId}`);
        // if (!response.status) {
        //   throw new Error("Contact not found");
        // }
        navigate(route === "view" ? `/contacts/view/${contactId}` : `/add-contact/${contactId}`);
      } catch (error) {
        console.error(`Error navigating to ${route} contact ID ${contactId}:`, error);
        const errorMessage = error.message.includes("404")
          ? "Contact not found. This may be due to the contact being inactive."
          : `Failed to ${route === "view" ? "view" : "edit"} contact: ${error.message}`;
        showErrorToast(errorMessage);
      }
    },
    [navigate]
  );

  const summaryData = useMemo(
    () => [
      {
        iconClass: "fas fa-users text-primary",
        label: "Total Contacts",
        value: contactsCount.totalCounts,
      },
      {
        iconClass: "fas fa-user-check text-primary",
        label: "Active Contacts",
        value: contactsCount.activeCount,
      },
      {
        iconClass: "fas fa-user-times text-danger",
        label: "Inactive Contacts",
        value: contactsCount.inactiveCount,
      },
      {
        iconClass: "fas fa-user-star text-warning",
        label: "Primary Contacts",
        value: contactsCount.primaryContacts,
      },
    ],
    [contactsCount]
  );

  useEffect(() => {
    fetchContactsData(searchQuery, currentPage, filterBy);
    fetchCounts();
  }, [fetchContactsData, searchQuery, currentPage, filterBy]);

  useEffect(() => {
    console.log("Contacts Data:", contactsData.contacts);
    console.log("Contacts Count State:", contactsCount);
    console.log("Auth Token:", localStorage.getItem("token"));
  }, [contactsData, contactsCount]);

  const getStatusLabelAndColor = (status) => {
    switch (Number(status)) {
      case 1:
        return { label: "", color: "text-success fw-semibold" };
      case 0:
        return { label: "", color: "text-secondary fw-semibold" };
      default:
        return {
          label: ``,
          color: "text-danger fw-semibold",
        };
    }
  };

  return (
    <div className="page-content">
      <Container fluid={true} className="py-0 px-0">
        <PageSubHeader
          heading={"Contacts"}
          routePath={"Home"}
          pageName={"Contacts"}
        />
        <Row className="px-4">
          <SummaryCards data={summaryData} />
          {apiStatus.error && (
            <div className="text-danger text-center m-3">{apiStatus.error}</div>
          )}
          <Row>
            <Col>
              <Row className="g-2 align-items-center py-2">
                <Col xs={12} md={3}>
                  <InputGroup size="sm" className="elbert-search-group">
                    <Input
                      placeholder="Search, ID, Name, Email"
                      className="elbert-search"
                      onChange={handleSearch}
                      value={searchQuery}
                    />
                    <InputGroupText>
                      <span className="fa fa-search"></span>
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col xs={12} md={3}>
                  <InputGroup size="sm" className="elbert-search-group">
                    <Input
                      type="select"
                      value={filterBy}
                      onChange={(e) => handleFilter(e.target.value)}
                      className="form-control form-select"
                    >
                      <option value="All">All</option>
                      <option value="Customer">Customer</option>
                      <option value="Vendor">Vendor</option>
                      <option value="None">Others</option>
                    </Input>
                  </InputGroup>
                </Col>
                <Col
                  xs={12}
                  md="auto"
                  className="ms-md-auto d-flex justify-content-md-end"
                >
                  <CustomButton
                    text="+ Add New"
                    onClick={() => navigate("/add-contact")}
                    className="w-100 w-md-auto"
                  />
                </Col>
              </Row>
              <Row>
                <Card>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <colgroup>
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "20%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "20%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "8%" }} />
                          <col style={{ width: "10%" }} />
                        </colgroup>
                        {renderTableHeader()}
                        <tbody>
                          {contactsData.contacts.map((contact, index) => {
                            const { label, color } = getStatusLabelAndColor(
                              contact.status
                            );
                            return (
                              <tr key={contact.id || index}>
                                <td>{contact.id || "N/A"}</td>
                                <td>
                                  {formatToPascal(
                                    `${contact.firstName} ${contact.lastName}`
                                  )}
                                </td>
                                <td>
                                  {(() => {
                                    if (
                                      contact.contactRelatedTo?.toLowerCase() ===
                                        "vendor" &&
                                      contact.vendorId
                                    ) {
                                      const vendor = vendors.find(
                                        (v) => v.value === contact.vendorId
                                      );
                                      return formatToPascal(
                                        vendor?.companyName ||
                                          contact.vendorName ||
                                          "N/A"
                                      );
                                    } else if (
                                      contact.contactRelatedTo?.toLowerCase() ===
                                        "customer" &&
                                      contact.customerId
                                    ) {
                                      const customer = customers.find(
                                        (c) => c.value === contact.customerId
                                      );
                                      return formatToPascal(
                                        customer?.companyName ||
                                          contact.customerName ||
                                          "N/A"
                                      );
                                    } else {
                                      return formatToPascal(
                                        contact.customerName ||
                                          contact.vendorName ||
                                          "N/A"
                                      );
                                    }
                                  })()}
                                </td>
                                <td>{contact.email || "N/A"}</td>
                                <td>{contact.phoneNumber || "N/A"}</td>
                                <td>
                                  {formatToPascal(contact.designation || "N/A")}
                                </td>
                                <td>
                                  <FormGroup switch>
                                    <Label
                                      for={`contactStatus-${contact.id}`}
                                      className={color}
                                    >
                                      {label}
                                    </Label>
                                    <Input
                                      style={{
                                        color: "blue",
                                        height: "1.4em",
                                        width: "3em",
                                        cursor:
                                          apiStatus.loading &&
                                          apiStatus.errorContactId ===
                                            contact.id
                                            ? "not-allowed"
                                            : "pointer",
                                      }}
                                      type="switch"
                                      disabled={
                                        apiStatus.loading &&
                                        apiStatus.errorContactId === contact.id
                                      }
                                      id={`contactStatus-${contact.id}`}
                                      checked={contact.status === 1}
                                      onChange={() => {
                                        console.log(
                                          "Switch toggled for contact:",
                                          contact.id,
                                          "Current Status:",
                                          contact.status
                                        );
                                        toggleContactStatus(
                                          contact.id,
                                          contact.status
                                        );
                                      }}
                                    />
                                    {apiStatus.loading &&
                                      apiStatus.errorContactId ===
                                        contact.id && <LoadingSpinner />}
                                  </FormGroup>
                                </td>
                                <td>
                                  <div className="d-flex gap-3 align-items-center">
                                    <i
                                      className="fa-solid fa-eye action-icon"
                                      title="View"
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        handleNavigation(
                                          contact.id,
                                          contact.status,
                                          "view"
                                        )
                                      }
                                    ></i>
                                    <i
                                      className="fa-regular fa-pen-to-square action-icon"
                                      title="Edit"
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        handleNavigation(
                                          contact.id,
                                          contact.status,
                                          "edit"
                                        )
                                      }
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    {contactsData.error && (
                      <div>
                        <p className="text-danger text-center m-3">
                          {contactsData.error}
                        </p>
                      </div>
                    )}
                    {!apiStatus.loading && !contactsData.contacts.length && (
                      <p className="text-center m-3">No contacts data found</p>
                    )}
                  </CardBody>
                </Card>
              </Row>
            </Col>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Row>
      </Container>
    </div>
  );
}

export default Contacts;